import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { DOCUMENTS, HANDBOOK_UPDATED, HANDBOOK_VERSION } from "../docs";
import { Markdown, outlineOf } from "../components/Markdown";

function download(name, text) {
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * The documentation reader: the executive overview and the technical handbook,
 * rendered from the repository's Markdown with Mermaid diagrams. "Export PDF"
 * prints the open chapter (or every chapter) through the browser's PDF printer
 * with the print stylesheet in index.css; the same Markdown is exported to PDF
 * on the command line by tools/docs/build_docs.sh.
 */
export function DocsPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [printAll, setPrintAll] = useState(false);

  const current = useMemo(() => DOCUMENTS.find((d) => d.id === docId) || DOCUMENTS[0], [docId]);
  const outline = useMemo(() => (current ? outlineOf(current.text) : []), [current]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return DOCUMENTS;
    return DOCUMENTS.filter((d) => d.title.toLowerCase().includes(q) || d.text.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    if (!docId && DOCUMENTS[0]) navigate(`/docs/${DOCUMENTS[0].id}`, { replace: true });
  }, [docId, navigate]);

  useEffect(() => {
    if (!printAll) return undefined;
    // give the diagrams a moment to render before the print dialog opens
    const t = setTimeout(() => {
      window.print();
      setPrintAll(false);
    }, 1500);
    return () => clearTimeout(t);
  }, [printAll]);

  useEffect(() => {
    document.title = current ? `${current.title} · ainaopen vision docs` : "ainaopen vision docs";
    return () => {
      document.title = "ainaopen vision";
    };
  }, [current]);

  if (!current) {
    return (
      <div className="card">
        <h1 className="text-xl font-semibold">Documentation</h1>
        <p className="text-sm text-slate-500">No documents are bundled in this build.</p>
      </div>
    );
  }

  const groups = [...new Set(visible.map((d) => d.group))];

  return (
    <div className="docs-page -m-6 flex min-h-[calc(100vh-3.5rem)]">
      <aside className="docs-sidebar hidden w-72 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex print:hidden">
        <div className="border-b border-slate-100 p-3">
          <input
            className="input"
            placeholder="Search the docs…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search documentation"
          />
          <div className="mt-2 text-[11px] text-slate-400">
            Handbook v{HANDBOOK_VERSION} · updated {HANDBOOK_UPDATED}
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 text-sm">
          {groups.map((g) => (
            <div key={g} className="mb-3">
              <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{g}</div>
              {visible
                .filter((d) => d.group === g)
                .map((d) => (
                  <Link
                    key={d.id}
                    to={`/docs/${d.id}`}
                    className={clsx(
                      "block rounded px-2 py-1.5 text-slate-700 hover:bg-slate-100",
                      d.id === current.id && "bg-brand-50 font-medium text-brand-700 hover:bg-brand-50"
                    )}
                  >
                    {d.part ? <span className="mr-1 text-slate-400">{d.part}</span> : null}
                    {d.title}
                  </Link>
                ))}
            </div>
          ))}
          {visible.length === 0 && <div className="px-2 text-slate-500">Nothing matches.</div>}
        </nav>
      </aside>

      <div className="min-w-0 flex-1">
        <div className="docs-toolbar sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white/95 px-6 py-2 backdrop-blur print:hidden">
          <div className="min-w-0 flex-1 truncate text-sm text-slate-500">
            {current.group} · <span className="text-slate-800">{current.title}</span>
          </div>
          <select
            className="input w-auto lg:hidden"
            value={current.id}
            onChange={(e) => navigate(`/docs/${e.target.value}`)}
            aria-label="Open a document"
          >
            {DOCUMENTS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.title}
              </option>
            ))}
          </select>
          <button type="button" className="btn-secondary" onClick={() => download(current.file, current.text)}>
            Markdown
          </button>
          <button type="button" className="btn-secondary" onClick={() => window.print()}>
            Export PDF
          </button>
          <button type="button" className="btn-primary" onClick={() => setPrintAll(true)} title="Every chapter in one PDF">
            Export all
          </button>
        </div>

        <div className="flex">
          <article className="docs-article min-w-0 flex-1 px-6 py-6 lg:px-10">
            {printAll ? (
              DOCUMENTS.map((d) => (
                <section key={d.id} className="docs-print-section">
                  <Markdown text={d.text} />
                </section>
              ))
            ) : (
              <Markdown text={current.text} />
            )}
          </article>
          {outline.length > 2 && !printAll && (
            <aside className="docs-outline sticky top-12 hidden h-[calc(100vh-6.5rem)] w-64 shrink-0 overflow-y-auto border-l border-slate-100 p-4 text-xs xl:block print:hidden">
              <div className="mb-2 font-semibold uppercase tracking-wide text-slate-400">On this page</div>
              {outline.map((h) => (
                <a
                  key={h.id + h.title}
                  href={`#${h.id}`}
                  className={clsx("block py-0.5 text-slate-600 hover:text-brand-700", h.level === 3 && "pl-3 text-slate-500")}
                >
                  {h.title}
                </a>
              ))}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
